import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback, ReactNode } from 'react';
import { supabase, SUPABASE_AUTH_STORAGE_KEY } from '@/lib/supabase';
import { clearBrandAccessCache, getUserBrands } from '@/lib/brandAccess';
import type { User as SupabaseUser } from '@supabase/supabase-js';

interface User extends SupabaseUser {
  role?: 'workspace_owner' | 'brand_owner' | 'contributor' | 'authenticated';
  full_name?: string;
}

interface Brand {
  id: string;
  name: string;
  owner_id: string;
  created_at: string;
  description?: string;
  role?: string;
  parent_brand_id?: string | null;
  is_sub_brand?: boolean;
  user_role?: string;
  is_passthrough_parent?: boolean;
}

interface Campaign {
  id: string;
  name: string;
  status: string;
  brand_id: string;
}

interface AppContextType {
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  user: User | null;
  loading: boolean;
  error: string | null;
  activeBrand: Brand | null;
  setActiveBrand: (brand: Brand | null) => void;
  brands: Brand[];
  loadingBrands: boolean;
  refreshBrands: () => Promise<void>;
  activeView: string;
  setActiveView: (view: string) => void;
  activeCampaign: Campaign | null;
  setActiveCampaign: (campaign: Campaign | null) => void;
  brandRefreshTrigger: number;
  campaignRefreshTrigger: number;
  contentRefreshTrigger: number;
  refreshContent: () => void;
  triggerBrandRefresh: () => void;
  triggerCampaignRefresh: () => void;
  triggerContentRefresh: () => void;
  fetchUserRole: () => Promise<void>;
  /**
   * Synchronous, non-blocking logout. Wipes the persisted token + resets
   * context state immediately, then fires `supabase.auth.signOut()` in
   * the background. Use this instead of awaiting `supabase.auth.signOut()`
   * directly — that call can hang indefinitely when the auth lock is
   * wedged, which would leave the UI showing the logged-in state forever.
   */
  forceLogout: () => void;
  openBrandSettings: (brandId: string) => void;
  activeSubBrand: Brand | null;
  setActiveSubBrand: (b: Brand | null) => void;
  subBrands: Brand[];
  /** The effective brand to use for all queries — sub-brand if selected, else parent */
  effectiveBrand: Brand | null;
  /** Whether the current user can edit the effectiveBrand (false = view-only passthrough) */
  effectiveBrandCanEdit: boolean;
  /** True once sub-brand auto-selection has settled for the active brand, so
   *  effectiveBrand won't flip from the parent to a sub-brand a moment later.
   *  Data-heavy views wait for it instead of loading the parent first. */
  brandContextReady: boolean;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const OWNER_ROLES = new Set(['workspace_owner', 'brand_owner', 'admin', 'owner']);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen]           = useState(true);
  const [user, setUser]                         = useState<User | null>(null);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState<string | null>(null);
  const [activeBrand, setActiveBrand]           = useState<Brand | null>(null);
  const [brands, setBrands]                     = useState<Brand[]>([]);
  const [loadingBrands, setLoadingBrands]       = useState(false);
  const [activeView, setActiveView]             = useState('dashboard');
  const [activeCampaign, setActiveCampaign]     = useState<Campaign | null>(null);
  const [brandRefreshTrigger, setBrandRefreshTrigger]       = useState(0);
  const [campaignRefreshTrigger, setCampaignRefreshTrigger] = useState(0);
  const [contentRefreshTrigger, setContentRefreshTrigger]   = useState(0);
  const [activeSubBrand, setActiveSubBrandState] = useState<Brand | null>(null);
  const [subBrands, setSubBrands]               = useState<Brand[]>([]);
  const [subBrandsResolvedFor, setSubBrandsResolvedFor] = useState<string | null>(null);

  const userRef          = useRef<User | null>(null);
  userRef.current        = user;
  const brandsLoadingRef = useRef(false);

  const toggleSidebar = useCallback(() => setSidebarOpen((prev) => !prev), []);

  const setActiveSubBrand = useCallback((b: Brand | null) => {
    setActiveSubBrandState(b);
  }, []);

  // ── Load sub-brands whenever activeBrand changes ──
  useEffect(() => {
    if (!activeBrand?.id) {
      setSubBrands([]);
      setActiveSubBrandState(null);
      return;
    }

    let cancelled = false;

    const loadSubBrands = async () => {
      try {
        const { data: allSubs, error: subsError } = await supabase
          .from('brands')
          .select('id, name, logo_url, description, created_at, owner_id, parent_brand_id, is_sub_brand')
          .eq('parent_brand_id', activeBrand.id)
          .eq('is_sub_brand', true)
          .eq('is_draft', false)
          .eq('is_archived', false)
          .order('name');

        if (cancelled) return;
        if (subsError) { console.error('[AppContext] Error loading sub-brands:', subsError); return; }
        if (!allSubs || allSubs.length === 0) { setSubBrands([]); return; }

        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (cancelled || !authUser) { setSubBrands(allSubs); return; }

        const { data: publicUser } = await supabase
          .from('users')
          .select('id')
          .eq('auth_id', authUser.id)
          .maybeSingle();

        if (cancelled || !publicUser) { setSubBrands(allSubs); return; }

        // Determine the user's role on the PARENT brand
        const { data: parentMembership } = await supabase
          .from('brand_team_members')
          .select('role')
          .eq('user_id', publicUser.id)
          .eq('brand_id', activeBrand.id)
          .maybeSingle();

        if (cancelled) return;

        const parentRole = parentMembership?.role ?? null;
        const isOwnerLevel = OWNER_ROLES.has(parentRole ?? '');

        if (isOwnerLevel) {
          // Owners/admins see ALL sub-brands and choose manually
          setSubBrands(allSubs);
          return;
        }

        // Contributor or pure viewer on parent — only show sub-brands they're
        // explicitly assigned to via brand_team_members or user_brand_access
        const subIds = allSubs.map((s) => s.id);

        const [{ data: memberships }, { data: accessRows }] = await Promise.all([
          supabase
            .from('brand_team_members')
            .select('brand_id')
            .eq('user_id', publicUser.id)
            .in('brand_id', subIds),
          supabase
            .from('user_brand_access')
            .select('brand_id')
            .eq('user_id', publicUser.id)
            .eq('can_view', true)
            .in('brand_id', subIds),
        ]);

        if (cancelled) return;

        const allowedIds = new Set([
          ...(memberships ?? []).map((m: any) => m.brand_id),
          ...(accessRows  ?? []).map((a: any) => a.brand_id),
          ...allSubs.filter((s: any) => s.owner_id === publicUser.id).map((s: any) => s.id),
        ]);

        const filtered = allSubs.filter((s: any) => allowedIds.has(s.id));
        setSubBrands(filtered);

        if (filtered.length > 0) {
          // Auto-activate the first allowed sub-brand — contributors/viewers
          // should land on their sub-brand, not the parent context
          setActiveSubBrandState((current) => {
            if (current && filtered.find((s: any) => s.id === current.id)) return current;
            return filtered[0];
          });
        } else {
          // Pure viewer with no sub-brand access — leave on parent (view-only)
          setActiveSubBrandState(null);
        }

      } catch (err) {
        console.error('[AppContext] Error in loadSubBrands:', err);
      } finally {
        if (!cancelled) setSubBrandsResolvedFor(activeBrand.id);
      }
    };

    loadSubBrands();
    return () => { cancelled = true; };
  }, [activeBrand?.id, brandRefreshTrigger]);

  // ── fetchUserRole ──
  const fetchUserRole = useCallback(async (currentUser?: User) => {
    const targetUser = currentUser ?? userRef.current;
    if (!targetUser) return;

    try {
      const { data, error: roleError } = await supabase
        .from('users')
        .select('role, full_name, id')
        .eq('auth_id', targetUser.id)
        .maybeSingle();

      if (roleError) { console.error('[AppContext] Error fetching user role:', roleError); return; }

      if (data) {
        setUser((prev) => {
          if (!prev) return null;
          if (prev.role === data.role && prev.full_name === data.full_name) return prev;
          return { ...prev, role: data.role as any, full_name: data.full_name };
        });
      } else {
        const { data: newUser, error: insertError } = await supabase
          .from('users')
          .insert({
            auth_id:   targetUser.id,
            email:     targetUser.email!,
            full_name: targetUser.user_metadata?.full_name || targetUser.email?.split('@')[0] || 'User',
            role:      'contributor',
          })
          .select('role, full_name, id')
          .single();

        if (!insertError && newUser) {
          setUser((prev) => {
            if (!prev) return null;
            if (prev.role === newUser.role && prev.full_name === newUser.full_name) return prev;
            return { ...prev, role: newUser.role as any, full_name: newUser.full_name };
          });
        }
      }
    } catch (err) {
      console.error('[AppContext] Error in fetchUserRole:', err);
    }
  }, []);

  // ── loadBrands — loads TOP-LEVEL brands only; sub-brands handled separately ──
  const loadBrands = useCallback(async (currentUser: User | null) => {
    if (!currentUser) { setBrands([]); setActiveBrand(null); return; }
    if (brandsLoadingRef.current) return;

    try {
      brandsLoadingRef.current = true;
      setLoadingBrands(true);

      const allBrands = await getUserBrands();

      const topLevelBrands = allBrands.filter(
        (b: any) => !b.is_sub_brand && !b.parent_brand_id
      );
      const accessibleSubBrands = allBrands.filter(
        (b: any) => b.is_sub_brand || !!b.parent_brand_id
      );

      setBrands([...topLevelBrands, ...accessibleSubBrands]);

      setActiveBrand((current) => {
        if (topLevelBrands.length === 0) return null;

        // Brands the user can actually own/edit (not passthrough-only parents)
        const editableBrands = topLevelBrands.filter((b: any) => !b.is_passthrough_parent);

        if (current) {
          // Keep current selection if it still exists and is not a passthrough-only brand
          const stillExists = topLevelBrands.find((b: any) => b.id === current.id);
          if (stillExists && !stillExists.is_passthrough_parent) return stillExists;
        }

        const savedId = localStorage.getItem('activeBrandId');
        const saved   = savedId && editableBrands.find((b: any) => b.id === savedId);

        // Prefer a brand the user can edit; fall back to first top-level brand
        return saved || editableBrands[0] || topLevelBrands[0];
      });
    } catch (err) {
      console.error('[AppContext] Error loading brands:', err);
      setError('Failed to load brands');
      setBrands([]);
    } finally {
      brandsLoadingRef.current = false;
      setLoadingBrands(false);
    }
  }, []);

  const refreshBrands = useCallback(async () => {
    clearBrandAccessCache();
    await loadBrands(userRef.current);
  }, [loadBrands]);

  const openBrandSettings = useCallback((brandId: string) => {
    localStorage.setItem('activeBrandId', brandId);
    sessionStorage.setItem('brand_tab_intent', JSON.stringify({ brandId, tab: 'settings', ts: Date.now() }));
    setActiveView('brands');
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('openBrandGuidelines', { detail: { brandId, tab: 'settings' } }));
    }, 0);
  }, []);

  // Wipe every flavor of auth-related localStorage key. Exposed via
  // `forceLogout` and used internally on auth failure.
  const wipeLocalSbKeys = useCallback(() => {
    try {
      const PATTERNS = [
        SUPABASE_AUTH_STORAGE_KEY,
        'maloo-auth-token',          // catches old keys without project ref
        'sb-',                       // default supabase-js prefix
        'supabase.auth.token',       // pre-v2
      ];
      const keys = Object.keys(localStorage).filter((k) =>
        PATTERNS.some((p) => k === p || k.startsWith(p)),
      );
      console.log(`[AppContext] 🧹 wiping ${keys.length} auth-related localStorage keys:`, keys);
      keys.forEach((k) => localStorage.removeItem(k));
    } catch (e) {
      console.warn('[AppContext] localStorage wipe failed:', e);
    }
  }, []);

  // Synchronous logout. supabase.auth.signOut() can hang for minutes when
  // the v2 client's internal `navigator.locks` lock is wedged (which is
  // exactly the bug that was making logout silently no-op for us). So we
  // tear down state ourselves and fire signOut() without awaiting it —
  // worst case it eventually completes in the background, best case it's
  // hung but we've already nuked everything that matters.
  const forceLogout = useCallback(() => {
    console.log('[AppContext] 🚪 forceLogout');
    wipeLocalSbKeys();
    clearBrandAccessCache();
    setUser(null);
    setBrands([]);
    setActiveBrand(null);
    setSubBrands([]);
    setActiveSubBrandState(null);
    // Fire-and-forget — never await this.
    Promise.resolve()
      .then(() => supabase.auth.signOut({ scope: 'local' } as any))
      .catch(() => { /* lock wedge is fine, we've already cleared state */ });
  }, [wipeLocalSbKeys]);

  // ── Auth init ──
  // The supabase-js v2 client wraps getSession() in a `navigator.locks`
  // acquire. When that lock wedges (we've seen it on every page load in
  // this app even with a fresh token), getSession() hangs forever — and
  // racing it with a timeout doesn't help, because the underlying promise
  // keeps holding the lock. So we DON'T await getSession() at all here.
  // We read the persisted session directly from localStorage, decide
  // signed-in vs signed-out synchronously, and only then ask supabase-js
  // to catch up in the background via onAuthStateChange (no await).
  useEffect(() => {
    let mounted = true;
    const startedAt = Date.now();
    const ms = () => `${Date.now() - startedAt}ms`;

    console.log('[AppContext] 🟢 auth init starting');

    type PersistedSession = {
      access_token?: string;
      refresh_token?: string;
      expires_at?: number;
      user?: { id: string; email?: string; user_metadata?: any };
    } | null;

    const readPersisted = (): PersistedSession => {
      try {
        const raw = localStorage.getItem(SUPABASE_AUTH_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        // supabase-js v2 may wrap as { currentSession, expiresAt } or store
        // the session object flat. Support both.
        const sess = parsed?.currentSession ?? parsed;
        if (!sess?.access_token || !sess?.user) return null;
        return sess as PersistedSession;
      } catch (e) {
        console.warn('[AppContext] localStorage parse failed:', e);
        return null;
      }
    };

    const isExpired = (sess: PersistedSession): boolean => {
      if (!sess?.expires_at) return false;
      // expires_at is unix seconds. Treat as expired 10s before nominal
      // expiry to avoid a borderline-expired token poisoning anything.
      return sess.expires_at * 1000 < Date.now() + 10_000;
    };

    const persisted = readPersisted();
    console.log(`[AppContext] step 1 @${ms()} — localStorage read: hasSession=${!!persisted}, expired=${persisted ? isExpired(persisted) : 'n/a'}`);

    if (persisted && !isExpired(persisted)) {
      // Optimistically restore. If supabase-js's background refresh later
      // determines this token is invalid, onAuthStateChange will fire
      // SIGNED_OUT and we'll clear state then.
      console.log(`[AppContext] step 2 @${ms()} — restoring user from localStorage`);
      setUser(persisted.user as User);
      // Fetch role in background — never blocks the UI.
      Promise.resolve()
        .then(() => fetchUserRole(persisted.user as User))
        .catch((err) => console.warn('[AppContext] fetchUserRole error:', err?.message));
    } else {
      if (persisted) {
        console.log(`[AppContext] step 2 @${ms()} — persisted session is expired, clearing`);
        wipeLocalSbKeys();
      } else {
        console.log(`[AppContext] step 2 @${ms()} — no persisted session, signed out`);
      }
      setUser(null);
    }

    setLoading(false);
    console.log(`[AppContext] 🏁 boot done @${ms()} — setLoading(false)`);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log(`[AppContext] 📣 onAuthStateChange event=${event} @${ms()}, mounted=${mounted}, hasSession=${!!session?.user}`);
      if (!mounted) return;

      if (event === 'TOKEN_REFRESHED') return;

      if (event !== 'INITIAL_SESSION') {
        if (event === 'SIGNED_IN' && session?.user) {
          const typedUser = session.user as User;
          setUser((prev) => {
            if (prev?.id === typedUser.id) return prev;
            return typedUser;
          });
          if (userRef.current?.id !== typedUser.id) await fetchUserRole(typedUser);
        } else if (event === 'SIGNED_OUT') {
          clearBrandAccessCache();
          setUser(null);
          setBrands([]);
          setActiveBrand(null);
          setSubBrands([]);
          setActiveSubBrandState(null);
        }
      }

      setLoading(false);
    });

    return () => {
      console.log(`[AppContext] 🔴 effect cleanup @${ms()}`);
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (user?.id) loadBrands(user);
    else if (!user) { setBrands([]); setActiveBrand(null); }
  }, [user?.id, brandRefreshTrigger, loadBrands]);

  const refreshContent         = useCallback(() => setContentRefreshTrigger((p) => p + 1), []);
  const triggerBrandRefresh    = useCallback(() => setBrandRefreshTrigger((p) => p + 1), []);
  const triggerCampaignRefresh = useCallback(() => setCampaignRefreshTrigger((p) => p + 1), []);
  const triggerContentRefresh  = useCallback(() => setContentRefreshTrigger((p) => p + 1), []);

  // ── effectiveBrand — sub-brand takes precedence over parent ──
  const effectiveBrand = useMemo(
    () => activeSubBrand ?? activeBrand,
    [activeSubBrand, activeBrand]
  );

  // ── effectiveBrandCanEdit — false when the brand is a passthrough-only parent ──
  const effectiveBrandCanEdit = useMemo(() => {
    if (!effectiveBrand) return false;
    if ((effectiveBrand as any).is_passthrough_parent) return false;
    const role = (effectiveBrand as any).user_role ?? (effectiveBrand as any).role ?? '';
    // If no role info at all, assume the user has access (they loaded the brand)
    // Only explicitly block viewers
    if (role === 'viewer') return false;
    return true;
  }, [effectiveBrand]);

  const brandContextReady = !activeBrand || subBrandsResolvedFor === activeBrand.id;

  const contextValue = useMemo(
    () => ({
      sidebarOpen,
      toggleSidebar,
      user,
      loading,
      error,
      activeBrand,
      setActiveBrand,
      brands,
      loadingBrands,
      refreshBrands,
      activeView,
      setActiveView,
      activeCampaign,
      setActiveCampaign,
      brandRefreshTrigger,
      campaignRefreshTrigger,
      contentRefreshTrigger,
      openBrandSettings,
      refreshContent,
      triggerBrandRefresh,
      triggerCampaignRefresh,
      triggerContentRefresh,
      fetchUserRole,
      forceLogout,
      activeSubBrand,
      setActiveSubBrand,
      subBrands,
      effectiveBrand,
      effectiveBrandCanEdit,
      brandContextReady,
    }),
    [
      sidebarOpen, toggleSidebar, user, loading, error,
      activeBrand, brands, loadingBrands, refreshBrands,
      activeView, activeCampaign,
      brandRefreshTrigger, campaignRefreshTrigger, contentRefreshTrigger,
      openBrandSettings, refreshContent,
      triggerBrandRefresh, triggerCampaignRefresh, triggerContentRefresh,
      fetchUserRole, forceLogout,
      activeSubBrand, setActiveSubBrand, subBrands,
      effectiveBrand, effectiveBrandCanEdit, brandContextReady,
    ]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto" />
          <p className="text-sm text-muted-foreground">Loading Maloo...</p>
        </div>
      </div>
    );
  }

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
};

export function useAppContext() {
  const context = useContext(AppContext);
  if (context === undefined) throw new Error('useAppContext must be used within an AppProvider');
  return context;
}